import { useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { FilterDrawer } from '@/components/ui/FilterDrawer'
import { useDebouncedValue } from './useDebouncedValue'
import { cn } from '@/lib/cn'

type StatusValue = string

interface StatusOption { value: StatusValue; label: string }

/**
 * Campo extra opcional renderizado no drawer (ex.: CPF com máscara). O valor é
 * mantido "cru" no draft; `mask` formata só para exibição e `toValue` normaliza
 * o que vai para o filtro (ex.: remover pontuação).
 */
export interface ExtraFilterField {
  /** Chave do filtro enviada ao backend (ex.: 'cpf[like]'). */
  key: string
  label: string
  placeholder?: string
  /** Máscara de exibição (recebe o valor cru). */
  mask?: (raw: string) => string
  /** Normaliza o valor digitado antes de guardar (ex.: só dígitos). */
  toValue?: (input: string) => string
  /** Quando definido, renderiza um Select (em vez de Input) com estas opções. */
  options?: { value: string; label: string }[]
  /**
   * Com `options`, renderiza um grupo de botões (segmented control, igual ao de
   * Status) em vez de um Select. Inclua `{ value: '', label: 'Todos' }`.
   */
  buttonGroup?: boolean
}

interface Options {
  /** Placeholder do campo de busca dentro do drawer. */
  searchPlaceholder?: string
  /** Filtro de status inicial. Default: 'ATIVO' (apenas ativos). */
  initialStatus?: StatusValue
  /**
   * Opções do grupo de botões de status. Default: Todos | Ativos | Inativos.
   * Use para domínios de status diferentes (ex.: credenciamento — EM ANDAMENTO
   * / ACEITO / REJEITADO). A opção de valor '' representa "Todos".
   */
  statusOptions?: StatusOption[]
  /** Campos extras renderizados no drawer (ex.: CPF). */
  extraFields?: ExtraFilterField[]
}

const DEFAULT_STATUS_OPTIONS: StatusOption[] = [
  { value: '',        label: 'Todos' },
  { value: 'ATIVO',   label: 'Ativos' },
  { value: 'INATIVO', label: 'Inativos' },
]

/**
 * Hook + drawer prontos para listas: busca textual + filtro de status
 * ATIVO/INATIVO, ambos DENTRO do drawer (regra global #2).
 *
 * Filtro de status agora é um **grupo de botões** (Todos | Ativos | Inativos),
 * com **Ativos** como padrão.
 *
 * Uso:
 *   const sf = useStatusFilter({ searchPlaceholder: 'Buscar especialidade' })
 *   const { rows, meta } = useCrudList(MyService, { filters: sf.filters })
 *   <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
 *   {sf.drawer}
 */
export function useStatusFilter(options: Options = {}) {
  // Default: ativos. Para listar tudo, passe `initialStatus: ''`.
  const { searchPlaceholder = 'Buscar', initialStatus = 'ATIVO', statusOptions = DEFAULT_STATUS_OPTIONS, extraFields = [] } = options

  const [isOpen, setIsOpen] = useState(false)
  const [searchDraft, setSearchDraft] = useState('')
  const [statusDraft, setStatusDraft] = useState<StatusValue>(initialStatus)
  const [searchApplied, setSearchApplied] = useState('')
  const [statusApplied, setStatusApplied] = useState<StatusValue>(initialStatus)
  const [extraDraft, setExtraDraft] = useState<Record<string, string>>({})
  const [extraApplied, setExtraApplied] = useState<Record<string, string>>({})

  // Busca aplicada com debounce para reduzir hits no backend caso o usuário
  // digite e feche o drawer rapidamente.
  const debouncedSearch = useDebouncedValue(searchApplied, 300)

  const filters = useMemo(() => {
    const f: Record<string, string> = {}
    if (debouncedSearch.trim()) f.search = debouncedSearch.trim()
    if (statusApplied) f.status = statusApplied
    for (const field of extraFields) {
      const v = (extraApplied[field.key] ?? '').trim()
      if (v) f[field.key] = v
    }
    return f
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusApplied, extraApplied])

  // Conta o status como filtro ativo só quando NÃO é o default (ATIVO).
  // Default == "comportamento esperado", então não infla o badge de filtros.
  const statusIsCustom = statusApplied !== initialStatus
  const extraActive = extraFields.reduce((n, f) => n + ((extraApplied[f.key] ?? '').trim() ? 1 : 0), 0)
  const activeCount = (debouncedSearch.trim() ? 1 : 0) + (statusIsCustom ? 1 : 0) + extraActive

  const drawer = (
    <FilterDrawer
      open={isOpen}
      onClose={() => setIsOpen(false)}
      activeCount={activeCount}
      onApply={() => {
        setSearchApplied(searchDraft)
        setStatusApplied(statusDraft)
        setExtraApplied(extraDraft)
      }}
      onClear={() => {
        setSearchDraft(''); setStatusDraft(initialStatus); setExtraDraft({})
        setSearchApplied(''); setStatusApplied(initialStatus); setExtraApplied({})
      }}
    >
      <Input
        label="Buscar"
        value={searchDraft}
        onChange={(e) => setSearchDraft(e.target.value)}
        placeholder={searchPlaceholder}
        iconLeft={<Search size={14} />}
      />
      {extraFields.map((field) => (
        field.options && field.buttonGroup ? (
          <ButtonGroupField
            key={field.key}
            label={field.label}
            value={extraDraft[field.key] ?? ''}
            onChange={(v) => setExtraDraft((prev) => ({ ...prev, [field.key]: v }))}
            options={field.options}
          />
        ) : field.options ? (
          <Select
            key={field.key}
            label={field.label}
            placeholder={field.placeholder ?? 'Todos'}
            value={extraDraft[field.key] ?? ''}
            onChange={(e) => setExtraDraft((prev) => ({ ...prev, [field.key]: e.target.value }))}
            options={field.options}
            searchable={false}
          />
        ) : (
          <Input
            key={field.key}
            label={field.label}
            placeholder={field.placeholder}
            value={field.mask ? field.mask(extraDraft[field.key] ?? '') : (extraDraft[field.key] ?? '')}
            onChange={(e) => {
              const raw = field.toValue ? field.toValue(e.target.value) : e.target.value
              setExtraDraft((prev) => ({ ...prev, [field.key]: raw }))
            }}
          />
        )
      ))}
      <ButtonGroupField label="Status" value={statusDraft} onChange={setStatusDraft} options={statusOptions} />
    </FilterDrawer>
  )

  return {
    open: () => {
      setSearchDraft(searchApplied)
      setStatusDraft(statusApplied)
      setExtraDraft(extraApplied)
      setIsOpen(true)
    },
    close: () => setIsOpen(false),
    filters,
    activeCount,
    drawer,
  }
}

/**
 * Grupo de botões para filtro (segmented control), ex.: [ Todos | Ativos |
 * Inativos ] ou [ Todos | Diretor | Afiliado ]. Borda externa única + divisões
 * internas. Reutilizado por Status e por extraFields com `buttonGroup`.
 */
function ButtonGroupField({ label, value, onChange, options }: {
  label: string
  value: StatusValue
  onChange: (v: StatusValue) => void
  options: StatusOption[]
}) {
  return (
    <div className="flex flex-col">
      <label className="text-xs font-medium text-gray-700 mb-1">{label}</label>
      <div className="inline-flex rounded-md ring-1 ring-gray-200 bg-white overflow-hidden">
        {options.map((opt, i) => {
          const active = value === opt.value
          return (
            <button
              key={opt.value || 'all'}
              type="button"
              onClick={() => onChange(opt.value)}
              aria-pressed={active}
              className={cn(
                'cursor-pointer flex-1 px-3 py-2 text-[13px] font-medium transition',
                i > 0 && 'border-l border-gray-200',
                active
                  ? 'bg-brand-500 text-white hover:bg-brand-600'
                  : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
